import * as enabledApi from '@shared/api/enabledPlayApi';
import { useProfilesStore } from '../state/ProfilesContainer';
import { publish } from './EventCallback';
import { buildFavoritesUpdateModel, favoritesDifferFromProfile } from './favoritesUtils';

/**
 * Persists macro favorites for a profile via the API. Must run in the main window (the single API
 * writer). The canonical profile from the profiles store is used as the base so concurrent edits to
 * other fields are preserved; the `macroId -> isFavorite` map is applied on top.
 *
 * Returns true when favorites are already in sync (no write needed) or the save succeeds; false when
 * the profile can't be found, is public (not the user's to update), or the API call fails.
 */
export async function persistFavorites(profileId: string, favorites: Record<string, boolean>): Promise<boolean> {
    const canonical = useProfilesStore.getState().allProfiles.find((p) => p.id === profileId);
    if (!canonical) return false;
    // Favorites on a public profile can't be persisted (the user doesn't own it) — leave them session-only.
    if (canonical.isPublic) return false;
    // Skip a no-op write when the stored profile already matches the panel's favorite state.
    if (!favoritesDifferFromProfile(canonical, favorites)) return true;

    const result = await enabledApi.updateProfile(profileId, buildFavoritesUpdateModel(canonical, favorites));
    if (result?.resultType === 'Ok' && result.data) {
        // Keep local stores in sync (mirrors EditProfileContainer.saveChanges).
        publish('userProfileUpdated', { profile: result.data });
        return true;
    }
    return false;
}
